import { DialogComponent } from "@components/Dialog";
import MySwitchField from "@components/form/switch";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { TaxRatesEntity } from "src/@types/catalog/taxRates";
import { CustomItemListItem } from "src/components/denseForm/customListItem/customItem";
import MenuAutocompleteV2 from "src/components/denseForm/MenuAutocompleteV2";
import {
  FormProvider,
  RHFAutocomplete,
  RHFTextField,
} from "src/components/hook-form";
import usePermissions from "src/hooks/usePermissions";
import { TaxesModal } from "src/pages/Catalog/Taxes/components";
import useItem from "src/pages/ManualDocumentV2/hooks/useItem";
import type { IItemDefaults } from "../../utils/defaultItemValues";
import { useItemsSchema } from "./itemsSchema";

export interface ISelectedLineItem {
  index: number | null;
  defaultItemValues: IItemDefaults;
}

interface IAddLineItemsDialogProps {
  isShowModal: boolean;
  showModalMethod: VoidFunction;
  itemAppend: (obj: IItemDefaults) => void;
  itemUpdate: (index: number, obj: IItemDefaults) => void;
  initialValues: ISelectedLineItem;
  taxRates: TaxRatesEntity[] | null;
  onTaxesRefresh: () => void;
  selectedComplianceStandard?: string | null;
  translate: (text: any, options?: any) => string;
}

export const AddLineItemsDialog = (props: IAddLineItemsDialogProps) => {
  const {
    isShowModal,
    itemAppend,
    showModalMethod,
    itemUpdate,
    initialValues: { defaultItemValues, index },
    taxRates,
    onTaxesRefresh,
    selectedComplianceStandard,
    translate,
  } = props;

  const { permissionHandle } = usePermissions();
  const canWriteTax = permissionHandle(["Taxes_Write"]);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const { getValues, watch: watchMainForm } = useFormContext<any>();
  const headerPoNumber = watchMainForm("poNumber");
  const headerDeliveryNote = watchMainForm("deliveryNote");
  const isHeaderPOToggleOn = watchMainForm("isHeaderPOToggleOn");
  const isHeaderDNToggleOn = watchMainForm("isHeaderDNToggleOn");
  const hideItemPO = isHeaderPOToggleOn || !!headerPoNumber;
  const hideItemDN = isHeaderDNToggleOn || !!headerDeliveryNote;
  const { documentClass, customerCompanyID } = getValues();
  const isPOorDN =
    documentClass === "PurchaseOrder" || documentClass === "DeliveryNote";

  const { itemsSchema } = useItemsSchema({
    translate,
    isPOorDN,
  });

  const methods = useForm<IItemDefaults>({
    resolver: yupResolver(itemsSchema) as any,
    defaultValues: defaultItemValues,
  });

  const { handleSubmit, setValue, reset, watch } = methods;
  // For Invoice/others: keep taxType/vatPercentage in sync with selected taxId.
  // For PO/DN: user selects taxType then vatPercentage directly (old form UX).
  const selectedTaxId = watch("taxId");
  const selectedTaxType = watch("taxType");
  const vatPercentageWatch = watch("vatPercentage");

  useEffect(() => {
    if (isPOorDN || !taxRates) return;
    if (!selectedTaxId) {
      setValue("taxType", "");
      setValue("vatPercentage", 0);
      return;
    }
    const match = taxRates.find((rate) => rate.taxId === selectedTaxId);
    if (!match) return;
    setValue("taxType", match.taxType ?? "");
    setValue("vatPercentage", match.rate ?? 0);
  }, [selectedTaxId, taxRates, setValue, isPOorDN]);

  useEffect(() => {
    if (!isPOorDN || !taxRates || !selectedTaxType) return;
    const match = taxRates.find(
      (r) => r.taxType === selectedTaxType && r.rate === vatPercentageWatch,
    );
    if (match) {
      setValue("taxId", match.taxId);
    } else {
      setValue("taxId", null);
    }
  }, [selectedTaxType, vatPercentageWatch, taxRates, isPOorDN, setValue]);

  const { selectOption, setSelectOption, ...itemsListProps } = useItem({
    label: defaultItemValues?.referenceCode,
    value: defaultItemValues?.referenceCode,
    customerId: customerCompanyID,
  });

  const [optionalFieldsVisibility, setOptionalFieldsVisibility] = useState({
    isDiscountVisible: false,
    isPOVisible: false,
    isDNVisible: false,
  });

  const handleClose = () => {
    setIsTaxModalOpen(false);
    showModalMethod();
    reset();
  };

  const addItem = (values: IItemDefaults) => {
    // For PO/DN: derive taxId from the selected taxType + vatPercentage (old form behaviour).
    if (isPOorDN && taxRates) {
      const matched = taxRates.find(
        (r) => r.taxType === values.taxType && r.rate === values.vatPercentage,
      );
      values.taxId = matched?.taxId ?? null;
    }
    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === undefined || value === "") {
        delete (values as any)[key];
      }
    }
    // For PO/DN: if taxType was not entered, also strip vatPercentage (even if 0) and taxId.
    // vatPercentage = 0 is only valid when the user explicitly selected a tax type.
    if (isPOorDN && !("taxType" in values)) {
      delete (values as any)["vatPercentage"];
      delete (values as any)["taxId"];
    }
    if (index === null || index === undefined) itemAppend(values);
    else itemUpdate(index, values);
    handleClose();
  };

  useEffect(() => {
    if (isShowModal) {
      reset(defaultItemValues);
      const isDiscVisible = !!defaultItemValues?.dto;
      setOptionalFieldsVisibility({
        isDiscountVisible: isDiscVisible,
        isPOVisible: !!defaultItemValues.poNumber,
        isDNVisible: !!defaultItemValues?.deliveryNote,
      });
      setValue("isDiscountVisible", isDiscVisible);
    }
  }, [isShowModal, defaultItemValues, reset, setValue]);

  const isDiscountVisibleForm = watch("isDiscountVisible");
  useEffect(() => {
    if (isDiscountVisibleForm) {
      setValue("dtoType", "amount");
    }
  }, [isDiscountVisibleForm, setValue]);

  const { isDNVisible, isDiscountVisible, isPOVisible } =
    optionalFieldsVisibility;
  const taxableRates = (taxRates ?? []).filter(
    ({ isWithholdingTax }) => !isWithholdingTax,
  );

  const ADD_NEW_TAX_OPTION = {
    value: "__add_new_tax__",
    label: "add_new_tax",
  };

  const taxOptions = useMemo(
    () => [
      ...taxableRates.map(({ taxId, taxType, rate }) => ({
        value: taxId,
        label: `${taxType} (${rate}%)`,
      })),
      ADD_NEW_TAX_OPTION,
    ],
    [taxableRates],
  );

  const handleTaxCreated = () => {
    onTaxesRefresh();
  };

  return (
    <>
      <DialogComponent
        isShowModal={isShowModal}
        showModalMethod={handleClose}
        maxWidth="sm"
        fullWidth
        contentSx={{ p: 0 }}
        dialogTitle={translate(`add_line_item`)}
        dialogSubTitle={translate(
          "search_by_reference_code_or_description_or_add_new_item",
        )}
        dialogContent={
          <FormProvider methods={methods} onSubmit={handleSubmit(addItem)}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MenuAutocompleteV2
                    optionTitle={translate("Catalog item")}
                    getOptionLabel={(option: any) => option?.label ?? ""}
                    onChange={(_event, newValue) => {
                      setSelectOption({
                        label: newValue?.code,
                        value: newValue?.code,
                      });
                      setValue("description", newValue?.name ?? "");
                      setValue("unitPrice", newValue?.price ?? "");
                      setValue("referenceCode", newValue?.code ?? "");
                    }}
                    onInputChange={(event: any) => {
                      setSelectOption({
                        label: event?.target.value,
                        value: event?.target.value,
                      });
                      setValue("referenceCode", event?.target.value ?? "");
                    }}
                    renderOption={(props, option) => (
                      <CustomItemListItem {...props} option={option} />
                    )}
                    renderOptionHeader={() => <></>}
                    {...itemsListProps}
                    onSearch={itemsListProps.getAsyncOptions}
                    enableSearch={true}
                    placeholder={translate(
                      "search_by_reference_code_description_keywords",
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <RHFTextField
                    label={translate("referenceCode")}
                    name="referenceCode"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <RHFTextField
                    label={translate("description")}
                    name="description"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RHFTextField
                    name="unitPrice"
                    label={translate("unitPrice")}
                    type="text"
                    inputProps={{
                      inputMode: "decimal",
                      min: 0,
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (
                        !/[0-9.]/.test(e.key) &&
                        ![
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Home",
                          "End",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value.replace(/[^0-9.]/g, "");

                      // Prevent multiple decimal points
                      const parts = value.split(".");
                      if (parts.length > 2) {
                        value = `${parts[0]}.${parts.slice(1).join("")}`;
                      }
                      setValue(
                        "unitPrice",
                        value === "" ? null : (value as any),
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RHFTextField
                    name="quantity"
                    label={translate("quantity")}
                    type="text"
                    inputProps={{
                      inputMode: "decimal",
                      min: 0,
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (
                        !/[0-9.]/.test(e.key) &&
                        ![
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Home",
                          "End",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value.replace(/[^0-9.]/g, "");

                      // Prevent multiple decimal points
                      const parts = value.split(".");
                      if (parts.length > 2) {
                        value = `${parts[0]}.${parts.slice(1).join("")}`;
                      }
                      setValue(
                        "quantity",
                        value === "" ? null : (value as any),
                      );
                    }}
                  />
                </Grid>
                {isPOorDN ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <RHFAutocomplete
                        name="taxType"
                        label={translate("tax_type")}
                        options={
                          taxRates && taxRates.length > 0
                            ? [
                                ...new Set(
                                  taxRates
                                    .filter((r) => !r.isWithholdingTax)
                                    .map((r) => r.taxType),
                                ),
                              ].map((t) => ({ value: t, label: t }))
                            : [
                                { value: "VAT", label: "VAT" },
                                { value: "IGIC", label: "IGIC" },
                              ]
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {taxRates && taxRates.length > 0 ? (
                        <RHFAutocomplete
                          name="vatPercentage"
                          label={translate("tax_percentage")}
                          options={taxRates
                            .filter(
                              (r) =>
                                r.taxType === selectedTaxType &&
                                !r.isWithholdingTax,
                            )
                            .map((r) => ({
                              value: r.rate,
                              label: `${r.description} (${r.rate}%)`,
                            }))}
                        />
                      ) : (
                        <RHFTextField
                          name="vatPercentage"
                          label={translate("tax_percentage")}
                          type="number"
                        />
                      )}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <RHFAutocomplete
                        name="taxId"
                        label={translate("tax")}
                        options={taxOptions}
                        getOptionDisabled={(option: any) =>
                          option.value === "__add_new_tax__" && !canWriteTax
                        }
                        onExternalChange={(newValue: any) => {
                          if (newValue?.value === "__add_new_tax__") {
                            if (canWriteTax) setIsTaxModalOpen(true);
                            return;
                          }
                          setValue("taxId", newValue?.value ?? null);
                        }}
                        renderOption={(props, option) => {
                          const isAddButton =
                            option.value === "__add_new_tax__";
                          const isDisabled = isAddButton && !canWriteTax;

                          return (
                            <li
                              {...props}
                              key={option.value}
                              style={{
                                borderTop: isAddButton
                                  ? "1px solid #eee"
                                  : undefined,
                                opacity: isDisabled ? 0.4 : 1,
                              }}
                            >
                              {isAddButton ? (
                                <Tooltip
                                  title={
                                    isDisabled
                                      ? translate("taxes_permission_required")
                                      : ""
                                  }
                                  placement="right"
                                >
                                  <span
                                    style={{
                                      pointerEvents: "all",
                                      cursor: isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                      width: "100%",
                                    }}
                                    onMouseDown={(e) => {
                                      if (isDisabled) e.stopPropagation();
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      sx={{ alignItems: "center" }}
                                    >
                                      <Plus
                                        strokeWidth="1.5px"
                                        fontSize="20px"
                                      />
                                      <Typography variant="body2">
                                        {translate(option.label)}
                                      </Typography>
                                    </Stack>
                                  </span>
                                </Tooltip>
                              ) : (
                                translate(option.label)
                              )}
                            </li>
                          );
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ display: "flex", alignItems: "flex-end" }}
                >
                  <MySwitchField
                    checked={isDiscountVisible}
                    label={
                      <Typography variant="body2">
                        {translate(`add_discount`)}
                      </Typography>
                    }
                    onChange={() => {
                      const next = !optionalFieldsVisibility.isDiscountVisible;
                      setOptionalFieldsVisibility((prev) => ({
                        ...prev,
                        isDiscountVisible: next,
                      }));
                      setValue("isDiscountVisible", next);
                      setValue("dto", null);
                      setValue("dtoType", "amount");
                    }}
                    labelPlacement="end"
                    sx={{ alignItems: "center" }}
                  />
                </Grid>

                {isDiscountVisible && (
                  <>
                    <Grid item xs={6}>
                      <RHFAutocomplete
                        name="dtoType"
                        label={translate("discount_type")}
                        options={[
                          { value: "amount", label: translate("amount") },
                          {
                            value: "percentage",
                            label: translate("percentage"),
                          },
                        ]}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <RHFTextField
                        name="dto"
                        label={translate("discount_amount")}
                        inputProps={{
                          inputMode: "decimal",
                          min: 0,
                        }}
                        onKeyDown={(
                          e: React.KeyboardEvent<HTMLInputElement>,
                        ) => {
                          if (
                            !/[0-9.]/.test(e.key) &&
                            ![
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                              "Home",
                              "End",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          let raw = e.target.value.replace(/[^0-9.]/g, "");

                          // Prevent multiple decimal points
                          const parts = raw.split(".");
                          if (parts.length > 2) {
                            raw = `${parts[0]}.${parts.slice(1).join("")}`;
                          }

                          // Keep as string while typing so a trailing "." isn't
                          // dropped (parseFloat("1.") === 1). Yup coerces on submit.
                          setValue("dto", raw === "" ? null : (raw as any));
                        }}
                      />
                    </Grid>
                  </>
                )}

                {documentClass !== "PurchaseOrder" && (
                  <Grid container item xs={12} spacing={1}>
                    {(documentClass === "Invoice" ||
                      documentClass === "DeliveryNote") &&
                      (!hideItemPO || !hideItemDN) && (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1">
                              {translate("connected_documents")}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    {(documentClass === "Invoice" ||
                      documentClass === "DeliveryNote") &&
                      !hideItemPO && (
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: "flex", alignItems: "flex-end" }}
                        >
                          <MySwitchField
                            checked={isPOVisible}
                            label={
                              <Typography variant="body2">
                                {translate(`add_po_number`)}
                              </Typography>
                            }
                            onChange={() => {
                              setOptionalFieldsVisibility((prev) => ({
                                ...prev,
                                isPOVisible:
                                  !optionalFieldsVisibility.isPOVisible,
                              }));
                              setValue("poNumber", "");
                            }}
                            labelPlacement="end"
                            sx={{ alignItems: "center" }}
                          />
                        </Grid>
                      )}

                    {documentClass === "Invoice" && !hideItemDN && (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ display: "flex", alignItems: "flex-end" }}
                      >
                        <MySwitchField
                          checked={isDNVisible}
                          label={
                            <Typography variant="body2">
                              {translate(`add_delivery_note`)}
                            </Typography>
                          }
                          onChange={() => {
                            setOptionalFieldsVisibility((prev) => ({
                              ...prev,
                              isDNVisible:
                                !optionalFieldsVisibility.isDNVisible,
                            }));
                            setValue("deliveryNote", "");
                          }}
                          labelPlacement="end"
                          sx={{ alignItems: "center" }}
                        />
                      </Grid>
                    )}

                    {isPOVisible && !hideItemPO && (
                      <Grid item xs={12} md={6}>
                        <RHFTextField
                          name="poNumber"
                          label={translate("po_number")}
                          placeholder={translate("add_po_number")}
                        />
                      </Grid>
                    )}
                    {isDNVisible && !hideItemDN && (
                      <Grid item xs={12} md={6}>
                        <RHFTextField
                          name="deliveryNote"
                          label={translate("delivery_note")}
                          placeholder={translate("add_delivery_note")}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          </FormProvider>
        }
        dialogActions={
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit(addItem)}
            >
              {index === undefined || index === null
                ? translate("add_item")
                : translate("update_item")}
            </Button>
          </Box>
        }
      />
      <TaxesModal
        open={isTaxModalOpen}
        handleCloseModal={() => setIsTaxModalOpen(false)}
        onSuccess={handleTaxCreated}
        translate={translate}
        selectedComplianceStandard={selectedComplianceStandard ?? null}
        isWithHoldingTaxVisible={false}
      />
    </>
  );
};
